// app/src/screens/Result.jsx — deliver the video (handoff §6), pixel-close. READY badge, the
// player (letterboxed stage), the version chip from the genome's masters[] lineage, Remix/Share/
// Download, and the FULL refine UI (timeline, selection window, instruction box) built as an
// HONEST disabled frame — post-render editing wires in over the edit seam (D-follow-up); Apply
// says so rather than faking a re-render. Media URL: local rig /files; prod delivery = D2 seam
// (media.js returns null for gs:// — we show the honest "not fetchable yet" state).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, RotateCw, Share2, Download, Play } from 'lucide-react';
import { useDismiss } from '@common/dismiss.js';
import { brandPrefill, createBrand } from '@common/drafts.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { VideoStage } from '../components/ui.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { mediaUrl } from '../lib/media.js';

// PL1 webvideos are served from OUR origin (the /v1/runs/:id/webvideo/* route). Same base the api client
// uses ('' in dev — the Vite proxy carries /v1; the real origin on prod), mirrored from oauth.js.
const API_URL = import.meta.env.VITE_API_URL || '';

export default function Result({ runId }) {
  const { t } = useI18n();
  const { api } = useSession();
  const mobile = useIsMobile();
  const toast = useToast();
  const [run, setRun] = useState(null);
  const [err, setErr] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const versionsRef = useRef(null); // wraps the version chip + its popover (D-AUTH §6 dismiss)
  const closeVersions = useCallback(() => setShowVersions(false), []);
  useDismiss(versionsRef, closeVersions, { enabled: showVersions });
  const [refineTab, setRefineTab] = useState('timeline');
  const [saveBrand, setSaveBrand] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [edit, setEdit] = useState({ name: '', one_liner: '', tone: '' });
  const [saving, setSaving] = useState(false);
  const [src, setSrc] = useState(null); // the playable video URL (signed on prod, /files on the dev rig)
  const [webvideo, setWebvideo] = useState(null); // PL1: the tokened webvideo iframe URL (our origin, instant preview)
  const [exporting, setExporting] = useState(false); // PL1: an Export-MP4 job in flight (Part C)
  const [exportGate, setExportGate] = useState(null); // PL1: {credits} — export was priced, awaiting a confirm click
  const alive = useRef(true); // guards the export poll's setState after unmount
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    api.get(`runs/${encodeURIComponent(runId)}`).then((r) => {
      if (r.kind === 'ok') setRun(r.body);
      else setErr(r.error || t('common.error'));
    });
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  // resolve the finished video to a playable URL: ask the server for a short-lived SIGNED url
  // (prod, gs:// object); if that's unavailable (the dev rig has no bucket) fall back to the local
  // /files path; if neither, src stays null and the UI shows the honest "not fetchable" note.
  // Wrapped in loadMedia() so the <video> can SELF-HEAL when a signed url lapses (onVideoError).
  const mediaRetry = useRef(0); // bounds the self-heal so a genuinely-missing object can't loop
  const loadMedia = () => {
    const summary = (run && run.summary) || null;
    // PL1 (pipeline === 'pl1'): the create delivered a WEBVIDEO, not an MP4 — mint the owner-scoped embed
    // token and show the instant in-browser preview (our player, our origin). No <video> / signed URL.
    if (summary && (summary.pipeline === 'pl1' || summary.webvideo)) {
      setSrc(null);
      api.get(`runs/${encodeURIComponent(runId)}/webvideo-embed`).then((r) => {
        setWebvideo(r.kind === 'ok' && r.body.url ? `${API_URL || window.location.origin}${r.body.url}` : null);
      });
      return;
    }
    const finished = (summary && (summary.finished || summary.master)) || null;
    if (!finished) { setSrc(null); return; }
    api.get(`runs/${encodeURIComponent(runId)}/media`).then((r) => {
      setSrc(r.kind === 'ok' && r.body.url ? r.body.url : mediaUrl(finished, runId));
    });
  };
  useEffect(() => {
    if (!run) return;
    mediaRetry.current = 0;
    loadMedia();
  }, [run]); // eslint-disable-line react-hooks/exhaustive-deps

  // SELF-HEAL: a signed url is short-lived (media-api caps it ~1h). If the page sat open past
  // expiry and the browser then needs the network (a seek, an un-buffered play, or an evicted
  // buffer), <video> fires 'error' — mint a FRESH url and reload. Capped at 2 tries so a
  // genuinely-missing object degrades to the honest frame instead of re-fetching forever; a
  // successful play (onPlaying) refills the budget so a MUCH-later expiry heals too.
  const onVideoError = () => {
    if (mediaRetry.current >= 2) { setSrc(null); return; }
    mediaRetry.current += 1;
    loadMedia();
  };

  if (err) return <p className="muted">{err}</p>;
  if (!run) return <p className="muted">{t('common.loading')}</p>;

  const versions = Math.max(Array.isArray(run.masters) ? run.masters.length : 0, 1);
  const title = (run.run && (run.run.title || run.run.run_id)) || runId;
  // REAL lineage only: v{n} down to v1 from the genome's masters[] count — no invented notes or
  // timestamps (the old demo VERSION_NOTES faked "Punchier VO · 3 min ago" onto real runs).
  const vlist = Array.from({ length: versions }, (_, i) => ({ v: versions - i, current: i === 0 }));

  // Share = a REAL clipboard write (the one honest gap where doing it beats faking it).
  const shareLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
        toast(t('toast.share'));
      } else { toast(t('toast.shareFailed')); }
    } catch { toast(t('toast.shareFailed')); }
  };

  // PL1 Export MP4 (Part C): the create delivers a webvideo; MP4 is a user-triggered background render.
  // Export is a RENDER spend (fixed credit price), so it takes the standard confirm handshake: the first
  // POST (no confirm) is priced -> needs_confirm + the credit cost; the button then shows "Confirm export
  // · N credits" and a second click re-sends confirm:true to actually enqueue. The worker renders once +
  // mixes (proper duck) -> the signed MP4 route. Shown only for a PL1 webvideo result.
  async function startExport(confirm = false) {
    if (exporting) return;
    setExporting(true);
    const r = await api.post(`runs/${encodeURIComponent(runId)}/export`, confirm ? { confirm: true } : {});
    if (r.kind === 'needs_confirm') {
      setExporting(false);
      const credits = (r.quote && r.quote.credits && r.quote.credits.credits) || null;
      setExportGate({ credits });
      // toast the cost so the icon-only (mobile) path is honest too — the button/toast prompt a second click
      toast(t('result.exportConfirm', { credits }) || `Export to MP4 for ${credits != null ? credits + ' ' : ''}credits — tap again to confirm.`);
      return;
    }
    setExportGate(null);
    if (r.kind === 'insufficient_credits') { setExporting(false); toast(t('result.insufficientCredits') || 'Not enough credits to export.'); return; }
    if (!(r.kind === 'ok' && (r.body.job_id || r.body.ok))) { setExporting(false); toast((r && (r.error || (r.errors && r.errors[0]))) || t('common.error')); return; }
    // ENQUEUED: keep "Exporting…" TRUE and poll the real job state so the UI reflects reality — the render
    // takes minutes, so the button stays busy until the job is terminal, then re-enables (retry on failure).
    toast(t('result.exportStarted') || 'Exporting your MP4 — it will appear in My Content.');
    if (r.body.existing && !confirm) { /* deduped to an in-flight/finished job — still poll it */ }
    pollExport(r.body.job_id);
  }

  // poll an export job until it's terminal; reflect the outcome (self-heals the media on success, re-enables
  // the button on failure so the user can retry — the enqueue key folds the failed count, so a retry is fresh).
  function pollExport(jobId) {
    if (!jobId) { setExporting(false); return; }
    let tries = 0;
    const tick = async () => {
      if (!alive.current) return; // component unmounted — stop touching state
      tries += 1;
      const j = await api.get(`jobs/${encodeURIComponent(jobId)}`);
      const state = j.kind === 'ok' ? (j.body.state || (j.body.job && j.body.job.state)) : null;
      if (!alive.current) return;
      if (state === 'succeeded') { setExporting(false); toast(t('result.exportReady') || 'Your MP4 is ready — find it in My Content.'); loadMedia(); return; }
      if (state === 'failed' || state === 'cancelled' || state === 'refused') { setExporting(false); toast(t('result.exportFailed') || 'Export failed — you can try again.'); return; }
      if (tries >= 90) { setExporting(false); return; } // ~7.5 min ceiling, then release the button (job still runs)
      setTimeout(tick, 5000);
    };
    setTimeout(tick, 5000);
  }

  // save-as-brand (CREATE-1 §6, confirm-not-create): derive a PREFILL from this run's genome,
  // let the user tweak name/one-liner/tone, then a normal createBrand. No AI, no auto-save.
  async function openReview() {
    setReviewOpen(true);
    if (prefill) return;
    const r = await brandPrefill(api, runId);
    if (r.kind === 'ok' && r.body.prefill) {
      const p = r.body.prefill;
      setPrefill(p);
      setEdit({ name: p.name || '', one_liner: p.one_liner || '', tone: p.tone || '' });
    } else { setReviewOpen(false); toast(r.error || t('common.error')); }
  }
  async function saveAsBrand() {
    if (saving || !prefill) return;
    setSaving(true);
    const body = { ...prefill, name: (edit.name || '').trim() || prefill.name || t('result.untitledBrand'), one_liner: edit.one_liner, tone: edit.tone, source_run_id: runId };
    const r = await createBrand(api, body);
    setSaving(false);
    if (r.kind === 'ok') { setReviewOpen(false); setSaveBrand(false); toast(t('result.savedBrand')); }
    else { toast(r.error || (r.errors && r.errors[0]) || t('common.error')); }
  }

  // ---- the video stage (real <video> when the rig serves it; else the honest frame) ----
  const stage = (
    <VideoStage aspect="9:16" mobile={mobile} poster="var(--poster-mint)" frameRadius={12} shadow frameShadow={false} label={t('result.fitsRatio', { aspect: '9:16' })}>
      <div style={{ position: 'absolute', insetInlineStart: 15, top: 15, font: '700 9.5px var(--font-mono)', color: 'rgba(255,255,255,.9)', letterSpacing: '.08em', zIndex: 2 }}>{String(title).slice(0, 18).toUpperCase()}</div>
      {webvideo ? (
        // PL1 instant preview: our white-labeled player + composition, embedded from our origin.
        // SANDBOXED: the composition JS is LLM-authored (not trusted), so it runs with allow-scripts but
        // DELIBERATELY WITHOUT allow-same-origin — an opaque origin that cannot read our cookies, token,
        // localStorage, or script the parent app. (A CSP header on the webvideo route is the second layer.)
        <iframe src={webvideo} title={String(title)} sandbox="allow-scripts" allow="autoplay; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', border: 0, display: 'block' }} />
      ) : src ? (
        <video src={src} controls autoPlay muted loop onError={onVideoError} onPlaying={() => { mediaRetry.current = 0; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: 60, height: 60, borderRadius: 999, background: 'rgba(255,255,255,.22)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Play size={22} color="#fff" fill="#fff" strokeWidth={0} /></span>
          </div>
          <div style={{ position: 'absolute', insetInline: 0, bottom: 0, padding: 15, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,.3)' }}><div style={{ width: '22%', height: '100%', borderRadius: 999, background: '#fff' }} /></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', font: '600 10px var(--font-mono)', color: 'rgba(255,255,255,.85)' }}><span>0:06</span><span>0:28</span></div>
          </div>
        </>
      )}
    </VideoStage>
  );

  const actionBtn = (onClick, Icon, label, primary) => (
    <button className="q-btn press" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 7, background: primary ? 'var(--coral)' : 'var(--surface)', color: primary ? '#fff' : 'var(--ink)', border: primary ? 'none' : '1px solid var(--border-strong)', padding: '9px 16px', borderRadius: 999, font: '700 12.5px var(--font-body)', boxShadow: primary ? 'var(--shadow-cta-sm)' : 'none' }}>
      <Icon size={14} color={primary ? '#fff' : 'currentColor'} strokeWidth={primary ? 2 : 1.9} />{label}{primary ? ' ▾' : ''}
    </button>
  );
  // mobile: a row below the video (Remix flex + Share/Download icon-squares) — matches the mobile comp
  const iconSquare = (onClick, Icon) => (
    <button className="q-btn press" onClick={onClick} style={{ width: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 13, padding: '11px 0' }}><Icon size={16} color="var(--ink)" strokeWidth={1.9} /></button>
  );
  const mobileActions = (
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      <button className="q-btn press" onClick={() => toast(t('toast.remix'))} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--coral)', color: '#fff', padding: 11, borderRadius: 13, font: '700 12.5px var(--font-body)', boxShadow: 'var(--shadow-cta-sm)' }}><RotateCw size={14} color="#fff" strokeWidth={2} />{t('result.remix')}</button>
      {iconSquare(shareLink, Share2)}
      {webvideo ? iconSquare(() => startExport(!!exportGate), Download) : iconSquare(() => (src ? window.open(src, '_blank') : toast(t('result.notFetchableNote'))), Download)}
    </div>
  );

  // ---- the refine panel (full UI, honest-disabled) ----
  const refine = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: mobile ? 'auto' : 340 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, marginTop: mobile ? 18 : 0 }}>
        <div className="seg">
          <button className={'opt' + (refineTab === 'timeline' ? ' on' : '')} onClick={() => setRefineTab('timeline')} style={{ padding: '7px 16px', fontSize: 12 }}>{t('result.timeline')}</button>
          <button className={'opt' + (refineTab === 'scenes' ? ' on' : '')} onClick={() => setRefineTab('scenes')} style={{ padding: '7px 16px', fontSize: 12 }}>{t('result.scenes')}</button>
        </div>
        <span className="mono" style={{ font: '600 11px var(--font-mono)', color: 'var(--text-faint)' }}>{(mobile ? t('result.tapMobile') : t('result.pointDesktop')).toUpperCase()}</span>
      </div>
      {/* persistent honesty: the refine UI is a preview frame — editing wires in over the edit seam */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: '700 10px var(--font-mono)', color: 'var(--tangerine)', background: 'var(--tangerine-tint)', borderRadius: 999, padding: '5px 11px', letterSpacing: '.04em', textTransform: 'uppercase' }}>{t('result.previewChip')}</span>
      </div>
      {/* timeline strip with the coral selection window */}
      <div style={{ position: 'relative', height: mobile ? 50 : 58, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: mobile ? 13 : 14, padding: mobile ? 7 : 9, display: 'flex', gap: mobile ? 4 : 5, overflow: 'hidden' }}>
        <div style={{ flex: 1, borderRadius: 8, background: 'linear-gradient(160deg,#3DE0AE,#0E9E76)' }} />
        <div style={{ flex: 1, borderRadius: 8, background: 'linear-gradient(160deg,#9E86FF,#6A4DE8)' }} />
        <div style={{ position: 'absolute', insetInlineStart: '33%', top: mobile ? 4 : 5, bottom: mobile ? 4 : 5, width: '24%', border: '2.5px solid var(--coral)', borderRadius: 10, background: 'rgba(255,81,71,.09)' }} />
        <div style={{ flex: 1, borderRadius: 8, background: 'linear-gradient(160deg,#FF9E5C,#F1662A)' }} />
        <div style={{ flex: 1, borderRadius: 8, background: 'linear-gradient(160deg,#3DE0AE,#0E9E76)' }} />
      </div>
      {/* instruction box (coral outline) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, background: 'var(--surface)', border: '2px solid var(--coral)', borderRadius: 16, padding: '12px 12px 12px 16px', boxShadow: '0 3px 12px rgba(255,81,71,.1)' }}>
        <span className="mono" style={{ font: '700 11px var(--font-mono)', color: 'var(--coral)', flex: 'none' }}>0:06–0:11</span>
        <span style={{ font: '500 14px var(--font-body)', flex: 1 }}>{t('result.refineExample')}</span>
        <button className="q-btn press" onClick={() => toast(t('result.refineDisabledNote'))} style={{ background: 'var(--coral)', color: '#fff', padding: '9px 18px', borderRadius: 999, font: '700 12.5px var(--font-body)' }}>{t('result.apply')}</button>
      </div>
      <p className="mono" style={{ font: '400 12px var(--font-body)', color: 'var(--text-faint)', margin: '10px 2px 0' }}>{t('result.refineHelp')}</p>
      <div style={{ flex: 1 }} />
      {saveBrand && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, background: 'var(--save-brand-bg)', borderRadius: mobile ? 15 : 16, padding: mobile ? 13 : '15px 17px', marginTop: mobile ? 14 : 20 }}>
          <span style={{ width: mobile ? 34 : 38, height: mobile ? 34 : 38, borderRadius: 999, background: 'var(--avatar-grad)', flex: 'none' }} />
          <div style={{ flex: 1 }}>
            <div style={{ font: `700 ${mobile ? 13 : 14}px var(--font-body)` }}>{mobile ? t('result.saveBrandTitleMobile') : t('result.saveBrandTitle')}</div>
            <div style={{ font: `400 ${mobile ? 11 : 12}px var(--font-body)`, color: 'var(--terracotta-ink)' }}>{mobile ? t('result.saveBrandSubMobile') : t('result.saveBrandSub')}</div>
          </div>
          <button className="q-btn press" onClick={openReview} style={{ background: 'var(--ink)', color: '#fff', padding: mobile ? '9px 14px' : '10px 18px', borderRadius: 999, font: `700 ${mobile ? 11.5 : 12.5}px var(--font-body)` }}>{t('result.saveBrandReview')}</button>
          {!mobile && <button className="q-btn" onClick={() => setSaveBrand(false)} style={{ background: 'transparent', color: 'var(--text-divider)', fontSize: 17, padding: '2px 6px', border: 'none' }}>×</button>}
        </div>
      )}
    </div>
  );

  return (
    <div className="view">
      {/* header */}
      <div style={{ display: 'flex', alignItems: mobile ? 'center' : 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div ref={versionsRef} style={{ display: 'flex', alignItems: 'center', gap: mobile ? 9 : 11, position: 'relative' }}>
          <h2 className="h-section" style={{ margin: 0, fontSize: mobile ? 21 : 26, flex: mobile ? 1 : 'none', maxWidth: mobile ? 180 : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h2>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: mobile ? 5 : 6, font: `700 ${mobile ? 9.5 : 10.5}px var(--font-mono)`, background: 'var(--mint-tint)', color: 'var(--mint-ink)', borderRadius: 999, padding: mobile ? '5px 9px' : '5px 11px' }}><span style={{ width: mobile ? 6 : 7, height: mobile ? 6 : 7, borderRadius: 999, background: 'var(--mint)' }} />{t('result.ready').toUpperCase()}</span>
          {mobile ? (
            <span className="mono" style={{ font: '700 10px var(--font-mono)', color: 'var(--text-muted)', background: 'var(--fill-chip)', borderRadius: 999, padding: '5px 9px' }}>{t('result.version', { n: versions })}</span>
          ) : (
            <button className="q-btn press" onClick={() => setShowVersions(!showVersions)} style={{ display: 'flex', alignItems: 'center', gap: 5, font: '700 11px var(--font-mono)', color: 'var(--text-muted)', background: 'var(--fill-chip)', borderRadius: 999, padding: '6px 11px', border: 'none' }}><RotateCw size={12} color="var(--text-faint)" strokeWidth={2.2} />{t('result.version', { n: versions })} ▾</button>
          )}
          {!mobile && showVersions && (
            <div style={{ position: 'absolute', top: 40, insetInlineStart: 150, width: 256, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-pop)', padding: 8, zIndex: 30 }}>
              <div className="mono" style={{ font: '600 9.5px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', padding: '5px 8px 8px' }}>{t('result.versionHistory').toUpperCase()}</div>
              {vlist.map((v) => (
                <button key={v.v} className="q-btn" onClick={() => { setShowVersions(false); if (!v.current) toast(t('toast.restore')); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 9, borderRadius: 10, width: '100%', background: v.current ? 'var(--coral-wash)' : 'transparent', border: 'none', textAlign: 'start', cursor: 'pointer' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: v.current ? 'var(--coral)' : 'var(--chevron)', flex: 'none' }} />
                  <span style={{ flex: 1, font: `${v.current ? 700 : 600} 12.5px var(--font-body)` }}>{t('result.version', { n: v.v })}{v.current ? ` · ${t('result.versionCurrent')}` : ''}</span>
                  {!v.current && <span className="mono" style={{ font: '600 10.5px var(--font-mono)', color: 'var(--violet)' }}>{t('result.restore')}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {!mobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {actionBtn(() => toast(t('toast.remix')), RotateCw, t('result.remix'), true)}
            {actionBtn(shareLink, Share2, t('result.share'), false)}
            {webvideo
              ? actionBtn(() => startExport(!!exportGate), Download,
                  exporting ? (t('result.exporting') || 'Exporting…')
                    : exportGate ? `${t('result.confirmExport') || 'Confirm export'}${exportGate.credits != null ? ` · ${exportGate.credits} ${t('result.credits') || 'credits'}` : ''}`
                    : (t('result.exportMp4') || 'Export MP4'), false)
              : actionBtn(() => (src ? window.open(src, '_blank') : toast(t('result.notFetchableNote'))), Download, t('result.download'), false)}
          </div>
        )}
      </div>

      {/* body */}
      {mobile ? (
        <div style={{ marginTop: 14 }}>{stage}{mobileActions}{refine}</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, marginTop: 22, alignItems: 'flex-start' }}>
          {stage}{refine}
        </div>
      )}

      {!src && !webvideo && <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>{t('result.notFetchableNote')}</p>}

      {/* save-as-brand review (prefilled from the run's genome — confirm, then createBrand) */}
      <Sheet open={reviewOpen} onClose={() => setReviewOpen(false)} title={t('result.saveBrandSheetTitle')}>
        {!prefill ? (
          <p className="muted">{t('common.loading')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>{t('result.saveBrandSheetNote')}</p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('brandForm.name')}</span>
              <input value={edit.name} onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value }))} placeholder={t('brandForm.namePlaceholder')} style={{ padding: '11px 13px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)', font: '600 14px var(--font-body)', color: 'var(--ink)', outline: 'none' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('brandForm.oneLiner')}</span>
              <input value={edit.one_liner} onChange={(e) => setEdit((s) => ({ ...s, one_liner: e.target.value }))} placeholder={t('brandForm.oneLinerPlaceholder')} style={{ padding: '11px 13px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)', font: '500 13.5px var(--font-body)', color: 'var(--ink)', outline: 'none' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('brandForm.tone')}</span>
              <input value={edit.tone} onChange={(e) => setEdit((s) => ({ ...s, tone: e.target.value }))} placeholder={t('brandForm.tonePlaceholder')} style={{ padding: '11px 13px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)', font: '500 13.5px var(--font-body)', color: 'var(--ink)', outline: 'none' }} />
            </label>
            {Array.isArray(prefill.colours) && prefill.colours.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('brands.colours')}</span>
                <div style={{ display: 'flex', gap: 8 }}>{prefill.colours.map((c) => <span key={c} style={{ width: 32, height: 32, borderRadius: 9, background: c, border: '1px solid var(--border)' }} />)}</div>
              </div>
            )}
            {Array.isArray(prefill.products) && prefill.products.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="mono" style={{ font: '600 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('brands.tabProducts')}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{prefill.products.map((p, i) => <span key={i} className="mono" style={{ font: '600 11px var(--font-mono)', color: 'var(--mint)', background: 'var(--mint-tint)', borderRadius: 999, padding: '5px 11px' }}>{p.name}</span>)}</div>
              </div>
            )}
            <button className="q-btn press" disabled={saving} onClick={saveAsBrand} style={{ background: 'var(--coral)', color: '#fff', padding: 13, borderRadius: 13, font: '700 14px var(--font-body)', boxShadow: 'var(--shadow-cta-sm)', opacity: saving ? 0.6 : 1, marginTop: 4 }}>{t('result.saveBrandConfirm')}</button>
          </div>
        )}
      </Sheet>
    </div>
  );
}
